/**
 * React Query hooks for the integrations module.
 *
 * Endpoints (all gated by the `integrations` feature flag + RBAC):
 *   GET    /api/integrations/              - list connected integrations
 *   GET    /api/integrations/catalog/      - marketplace (available + connected)
 *   GET    /api/integrations/<uuid>/       - detail (credentials masked)
 *   POST   /api/integrations/              - connect a new provider
 *   PATCH  /api/integrations/<uuid>/       - update credentials / toggle active
 *   DELETE /api/integrations/<uuid>/       - soft-delete
 *   POST   /api/integrations/<uuid>/test/  - verify credentials work
 *
 * The axios instance auto-unwraps the `{status,message,result}` envelope,
 * so `res.data` is already the payload shape documented in
 * `types/integrations.ts`.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import api from '@/admin/lib/api'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import type {
  CatalogEntry,
  Integration,
  IntegrationCreatePayload,
  IntegrationDetail,
  IntegrationUpdatePayload,
  RewardIssuanceCancelPayload,
  RewardIssuanceRow,
  RewardRoutingOptions,
  RewardRulePayload,
  RewardRulesResponse,
  RewardsBalance,
  RewardsOptions,
  RewardsOptionsPayload,
  TestConnectionResult,
} from '@/admin/types/integrations'
import type { PaginatedResponse } from '@/shared/types/api'

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const INTEGRATIONS_KEYS = {
  all: ['integrations'] as const,
  list: () => [...INTEGRATIONS_KEYS.all, 'list'] as const,
  catalog: () => [...INTEGRATIONS_KEYS.all, 'catalog'] as const,
  detail: (uuid: string) => [...INTEGRATIONS_KEYS.all, 'detail', uuid] as const,
  rewardRules: () => [...INTEGRATIONS_KEYS.all, 'reward-rules'] as const,
  rewardIssuances: (params: Record<string, unknown>) =>
    [...INTEGRATIONS_KEYS.all, 'reward-issuances', params] as const,
  rewardRoutingOptions: () =>
    [...INTEGRATIONS_KEYS.all, 'reward-routing-options'] as const,
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useIntegrationsList() {
  const enabled = useFeatureFlag(FLAGS.INTEGRATIONS)
  return useQuery<Integration[]>({
    queryKey: INTEGRATIONS_KEYS.list(),
    queryFn: () =>
      api
        .get<PaginatedResponse<Integration>>('/integrations/', {
          params: { page_size: 200 },
        })
        .then((r) => r.data.results),
    enabled,
  })
}

export function useIntegrationsCatalog() {
  const enabled = useFeatureFlag(FLAGS.INTEGRATIONS)
  return useQuery<CatalogEntry[]>({
    queryKey: INTEGRATIONS_KEYS.catalog(),
    queryFn: () => api.get('/integrations/catalog/').then((r) => r.data),
    enabled,
  })
}

export function useIntegration(uuid: string | null | undefined) {
  const integrationsEnabled = useFeatureFlag(FLAGS.INTEGRATIONS)
  return useQuery<IntegrationDetail>({
    queryKey: INTEGRATIONS_KEYS.detail(uuid ?? ''),
    queryFn: () => api.get(`/integrations/${uuid}/`).then((r) => r.data),
    enabled: integrationsEnabled && !!uuid,
  })
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useConnectIntegration() {
  const qc = useQueryClient()
  return useMutation<IntegrationDetail, Error, IntegrationCreatePayload>({
    mutationFn: (payload) =>
      api.post('/integrations/', payload).then((r) => r.data),
    onSuccess: () => {
      // Catalog + list both reflect the new row.
      qc.invalidateQueries({ queryKey: INTEGRATIONS_KEYS.all })
    },
  })
}

/**
 * Probe a rewards provider with entered credentials, returning the selectable
 * customers / accounts / catalog so the connect screen can fetch-and-select.
 * Stores nothing - runs before the integration row exists.
 */
export function useFetchRewardsOptions() {
  return useMutation<RewardsOptions, Error, RewardsOptionsPayload>({
    mutationFn: (payload) =>
      api.post('/integrations/rewards/options/', payload).then((r) => r.data),
  })
}

/** Funded-account balance for the org's active rewards integration. */
export function useRewardsBalance() {
  return useQuery<RewardsBalance>({
    queryKey: [...INTEGRATIONS_KEYS.all, 'reward-balance'],
    queryFn: () => api.get('/integrations/rewards/balance/').then((r) => r.data),
    staleTime: 60_000,
    retry: false,
  })
}

/** Rewards activity ledger - what was issued, to whom, and its delivery status. */
export function useRewardIssuances(
  params: {
    page?: number
    page_size?: number
    status?: string
    search?: string
    created_after?: string
    created_before?: string
  } = {},
) {
  return useQuery<
    PaginatedResponse<RewardIssuanceRow> & {
      /** Rows per status, counted before the status filter. */
      status_counts?: Record<string, number>
    }
  >({
    queryKey: INTEGRATIONS_KEYS.rewardIssuances(params),
    queryFn: () =>
      api.get('/integrations/rewards/issuances/', { params }).then((r) => r.data),
    // Keeps the status pills on screen while a pill switch refetches.
    placeholderData: keepPreviousData,
  })
}

/**
 * Cancel a sent reward against the provider. On success the ledger row flips to
 * CANCELLED, so we invalidate every reward-issuances query (search/filter/page
 * variants all share the `reward-issuances` key prefix).
 */
export function useCancelRewardIssuance() {
  const qc = useQueryClient()
  return useMutation<
    RewardIssuanceRow,
    Error,
    { uuid: string; payload: RewardIssuanceCancelPayload }
  >({
    mutationFn: ({ uuid, payload }) =>
      api
        .post(`/integrations/rewards/issuances/${uuid}/cancel/`, payload)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...INTEGRATIONS_KEYS.all, 'reward-issuances'],
      })
      qc.invalidateQueries({
        queryKey: [...INTEGRATIONS_KEYS.all, 'reward-balance'],
      })
    },
  })
}

/** Reward rules (issue_reward side-effects) + the states they can hang off. */
export function useRewardRules() {
  return useQuery<RewardRulesResponse>({
    queryKey: INTEGRATIONS_KEYS.rewardRules(),
    queryFn: () => api.get('/integrations/rewards/rules/').then((r) => r.data),
  })
}

/**
 * Account / reward / template dropdown data for the rule builder. A live
 * provider read, so it is fetched lazily (the dialog enables it on open) and
 * cached - keeping the rules list + save path off the provider.
 */
export function useRewardRoutingOptions(opts: { enabled?: boolean } = {}) {
  return useQuery<RewardRoutingOptions>({
    queryKey: INTEGRATIONS_KEYS.rewardRoutingOptions(),
    queryFn: () =>
      api.get('/integrations/rewards/routing-options/').then((r) => r.data),
    enabled: opts.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpsertRewardRule() {
  const qc = useQueryClient()
  return useMutation<unknown, Error, RewardRulePayload>({
    mutationFn: (payload) =>
      api.post('/integrations/rewards/rules/', payload).then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: INTEGRATIONS_KEYS.rewardRules() }),
  })
}

export function useRemoveRewardRule() {
  const qc = useQueryClient()
  return useMutation<
    void,
    Error,
    { workflow_key: string; state_key: string; key?: string }
  >({
    mutationFn: ({ workflow_key, state_key, key }) =>
      api
        .delete('/integrations/rewards/rules/', {
          params: { workflow_key, state_key, ...(key ? { key } : {}) },
        })
        .then(() => undefined),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: INTEGRATIONS_KEYS.rewardRules() }),
  })
}

export function useUpdateIntegration(uuid: string) {
  const qc = useQueryClient()
  return useMutation<IntegrationDetail, Error, IntegrationUpdatePayload>({
    mutationFn: (payload) =>
      api.patch(`/integrations/${uuid}/`, payload).then((r) => r.data),
    onSuccess: (data) => {
      // Update the detail cache immediately to avoid a flash of stale data,
      // then invalidate siblings so list + catalog refetch in the background.
      qc.setQueryData(INTEGRATIONS_KEYS.detail(uuid), data)
      qc.invalidateQueries({ queryKey: INTEGRATIONS_KEYS.list() })
      qc.invalidateQueries({ queryKey: INTEGRATIONS_KEYS.catalog() })
    },
  })
}

export function useDisconnectIntegration() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (uuid) => api.delete(`/integrations/${uuid}/`).then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTEGRATIONS_KEYS.all })
    },
  })
}

export function useTestIntegrationConnection() {
  return useMutation<TestConnectionResult, Error, string>({
    mutationFn: (uuid) =>
      api.post(`/integrations/${uuid}/test/`).then((r) => r.data),
  })
}
